import React, { useState, useCallback, useContext } from 'react';

import SentencesComponent from 'codeable/SentencesComponent';
import ParagraphCodeList from 'codeable/ParagraphCodeList';
import ParagraphLayout from 'paragraph/ParagraphLayout';
import CodeOperationsContext from 'contexts/CodeOperationsContext';
import useTrackingPage from 'hooks/useTrackingPage';

const SpeakerParagraphComponent = ({
  codedParagraph,
  paragraphIndex,
  hasEditPermission,
  onParagraphClick,
  onEditClick,
  canEdit,
  isPrint,
  marginLeft,
  marginRight,
  title,
  color,
  logo,
  hideTitle,
  onTitleClick,
  setPrimeCode,
  removePrimeCode,
  primeCode,
  filteredExcerpts = [],
  showCodeSelector = false,
  selectedExcerptId = null,
  hasTextSelection = false,
  enableHover = false
}) => {
  const [paragraphHover, setParagraphHover] = useState(false);
  const [showTools, setShowTools] = useState(false);
  const [hoverExcerptId, setHoverExcerptId] = useState(null);

  const { onRemoveCode } = useContext(CodeOperationsContext) || {};
  const page = useTrackingPage();
  // × on a paragraph code tag is a code-tag surface, not the sidebar.
  const onTagRemove = useCallback(
    (codeID) => onRemoveCode?.(codeID, null, { page, surface: 'code_tag' }),
    [onRemoveCode, page]
  );

  const onHoverExcerpt = useCallback((excerptId) => {
    setHoverExcerptId(excerptId);
  }, []);

  const onMouseEnter = useCallback(() => {
    setShowTools(true);
  }, []);

  const onMouseLeave = useCallback(() => {
    setShowTools(false);
  }, []);

  const onParagraphHover = useCallback((hover) => {
    if ((isPrint && !enableHover) || !hasEditPermission) return;
    setParagraphHover(hover);
  }, [isPrint, enableHover, hasEditPermission]);

  const tagEnter = useCallback((codeID) => {
    if (setPrimeCode) {
      setPrimeCode(codeID);
    }
  }, [setPrimeCode]);

  const tagLeave = useCallback(() => {
    if (removePrimeCode) {
      removePrimeCode();
    }
  }, [removePrimeCode]);

  const onClick = useCallback((target, event) => {
    if (!hasEditPermission) return;
    onParagraphClick?.(codedParagraph.start, codedParagraph.end);
  }, [hasEditPermission, onParagraphClick, codedParagraph]);

  if (!codedParagraph || codedParagraph.end - codedParagraph.start <= 0) {
    return <div></div>;
  }

  const speaker = title || codedParagraph.speaker;
  const time = codedParagraph.time;
  const paragraphColor = color || codedParagraph.color || null;

  return (
    <ParagraphLayout
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      hasEditPermission={hasEditPermission}
      onParagraphHover={onParagraphHover}
      marginLeft={marginLeft}
      marginRight={marginRight}
      speaker={speaker}
      time={time}
      color={paragraphColor}
      paragraphIndex={paragraphIndex}
      start={codedParagraph.start}
      end={codedParagraph.end}
      logo={logo}
      onParagraphClick={onClick}
      onEditClick={onEditClick}
      canEdit={canEdit}
      isPrint={isPrint}
      showTools={showTools}
      hideTitle={hideTitle}
      onTitleClick={onTitleClick}
    >
      <div>
        <SentencesComponent
          sentences={codedParagraph.sentences}
          paragraphHover={paragraphHover}
          isPrint={isPrint}
          enableHover={enableHover}
          hasEditPermission={hasEditPermission}
          onHoverExcerpt={onHoverExcerpt}
          hoverExcerptId={hoverExcerptId}
          primeCode={primeCode}
        />

        <ParagraphCodeList
          codes={codedParagraph.codes}
          hasEditPermission={hasEditPermission}
          onCodeRemove={onTagRemove}
          tagEnter={tagEnter}
          tagLeave={tagLeave}
          activeCodeId={primeCode}
          filteredExcerpts={filteredExcerpts}
          showCodeSelector={showCodeSelector}
          selectedExcerptId={selectedExcerptId}
          hasTextSelection={hasTextSelection}
        />
      </div>
    </ParagraphLayout>
  );
};

export default SpeakerParagraphComponent;